import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionEntityType, TableViewResponse } from '@pmp/api';
import { useTableViewsStore } from '@pmp/stores';
import { createDeleteErrorException } from '@pmp/utils';
import { notification } from 'antd';

export const useTableView = (entityType: ActionEntityType) => {
  const {
    tableViews: { [entityType]: tableViews },
    getTableViews: getViews,
    saveTableView: saveViews,
    deleteTableView: deleteViews,
  } = useTableViewsStore();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const getTableViews = useCallback(() => {
    setIsLoading(true);
    return getViews(entityType)
      .catch(error => {
        console.error('Failed to load table views:', error);
        notification.error({
          title: t('global.errors.failedToLoadTableViews'),
        });
        setError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [entityType, getViews, t]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const saveTableView = useCallback((tableView: any) => saveViews(entityType, tableView as TableViewResponse), [entityType, saveViews]);

  const deleteTableView = useCallback(
    (id: string) => {
      return deleteViews(entityType, id)
        .then(() => {
          return { description: t('global.notifications.xDeletedSuccessfully', { x: t('global.labels.view') }) };
        })
        .catch(error => {
          return Promise.reject(createDeleteErrorException(error));
        });
    },
    [deleteViews, entityType, t],
  );

  useEffect(() => {
    if (!entityType || error || isLoading || tableViews) return;
    getTableViews();
  }, [entityType, error, getTableViews, getViews, isLoading, tableViews]);

  return { tableViews, isLoadingTableViews: isLoading, getTableViews, saveTableView, deleteTableView };
};
